import {
  CardWrapperWithHeader,
  chartProducts,
  chartRegistrations,
  csvExportConfigs,
  withCsvHeaders,
  FilterValueShape,
  LoadingSpinner,
} from '@rabbit/elements/shared-components';
import { useContext, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import {
  //ManufacturerHoldingWithVendableData,
  useGetHoldingList,
} from '@rabbit/bizproc/react';
import {
  formatDataChart,
  getRetailerById,
  ChartDataShape,
  getUserPermissions,
} from '../utils/helpers';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import TableRegistrations from '../components/molecules/TableRegistrations/TableRegistrations';
import { Navigate } from 'react-router-dom';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import {
  DTBusiness_User_Invite,
  DTHoldingProxy,
  DTRetailer_Public,
  Permissions,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  SELF_REGISTERED_VENDABLE,
  WarrantyStatus,
} from '@rabbit/data/types';
import {
  BL_Warranty,
  nestApiGetMainGroup,
  Permissions2string,
} from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';
import moment from 'moment';
import { useMemo } from 'react';

export type TenantType = 'carTenants' | 'tableTenants' | 'defaultTenants';

export enum TenantTypeEnum {
  DEFAUL_TENANT = 'defaultTenants',
  CAR_TENANT = 'carTenants',
  TABLE_TENANT = 'tableTenants',
}

interface ChartCountItemShape {
  id: string;
  count: number;
  label: string;
}

// Note: several parts of useGetHoldingList are commented out, as they handle pagination.
// For now all the data processing is being done here rather than in the back end.
// This will likely change in the future. - DC
export function RegistrationsView() {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const tenantLink = t('tenantLink');
  const userPermissions = getUserPermissions(tenantLink, userContext);
  const isPremiumTenant = userContext?.accessToken?.['W:' + tenantLink] === 'S';
  const {
    accessToken,
    repairerPersonaId,
    retailerPersonaId,
    installerPersonaId,
    consumerPersonaId,
    activeTenantInfo,
  } = userContext ?? ({} as any);

  const { config } = useContext(AppContext);

  // sorry about this one - DC
  // quick hack for preventing fatbikes repairers from accessing the registrations page
  // todo: create a nice tidy way to handle checking allowed routes and redirecting away from restricted ones
  const permissionsFromRootRepairer =
    accessToken[
      PersonaTypeSingleLetter.Repairer + PersonaIdTypeSplitter + t('tenantLink')
    ];

  /*if (
    permissionsFromRootRepairer &&
    !permissionsFromRootRepairer.includes(
      Permissions2string([Permissions.Owner])
    ) &&
    config.NAVIGATION.SAGE.DEFAULT_PARTNER_PATH &&
    config.NAVIGATION.SAGE.DEFAULT_PARTNER_PATH !==
      SAGE_ROUTE_NAME.REGISTRATIONS &&
    config.NAVIGATION.RESTRICTED_PATHS.includes(SAGE_ROUTE_NAME.REGISTRATIONS)
  ) {
    //navigate(config.NAVIGATION.SAGE.DEFAULT_PARTNER_PATH);
  }
*/
  // TODO: In the future it would be nice to get this directly from the context - DC
  const activePersonaIds = {
    repairer: repairerPersonaId,
    retailer: retailerPersonaId,
    installer: installerPersonaId,
    consumer: consumerPersonaId,
  };

  //Set the filters default date range to this month -VP
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const beginDate = new Date(currentYear, currentMonth, 1);
  const endDate = today;

  const monthlyBeginDate = moment(endDate).subtract(1, 'months').toDate();

  const [filters, setFilters] = useState<FilterValueShape>(() => {
    if (t('tenantLink') === 'WARRANTYIRELAND') {
      return {
        dateType: 'This month',
        beginDate: beginDate,
        endDate: endDate,
      };
    }

    if (config.HOLDINGS.REGISTRATION.SHOW_MONTHLY_DATA) {
      return {
        dateType: 'Date Range',
        beginDate: monthlyBeginDate,
        endDate: endDate,
      };
    }

    return {};
  });
  let tenantType: TenantType = 'defaultTenants';
  //const [pageSize, setPageSize] = useState(500);
  const {
    holdingList,
    // getNextPage,
    //totalHoldingCount,
    isLoading,
  } = useGetHoldingList(
    //pageSize,
    t('tenantLink'),
    accessToken,
    activePersonaIds,
    filters
  );

  const [chartMonthRegistrationsData, setChartMonthRegistrationsData] =
    useState<any[]>([]);
  const [chartRetailersData, setChartRetailersData] = useState<
    ChartDataShape[]
  >([]);
  const [sharedRetailerData, setSharedRetailerData] = useState<
    DTRetailer_Public[] | null
  >(null);
  const [sharedBusinessUserData, setSharedBusinessUserData] = useState<
    DTBusiness_User_Invite[] | null
  >(null);

  if (
    t('tenantLink') === 'WARRANTYIRELAND' ||
    t('tenantLink') === 'PINNACLEWARRANTIES'
  )
    tenantType = 'carTenants';
  if (t('tenantLink') === 'NUCOVER') tenantType = 'tableTenants';

  const onChangeFilters = (value: FilterValueShape) => {
    setFilters(value);
  };

  const chartOptionsMonthRegistrations = useMemo(
    () => withCsvHeaders(chartProducts.options, csvExportConfigs.productRegistrations),
    []
  );

  const chartOptionsRetailers = useMemo(
    () => withCsvHeaders(chartRegistrations.options, csvExportConfigs.registrationsByChannel),
    []
  );

  const handleRetailerDataLoaded = (
    retailerData: DTRetailer_Public[],
    businessUserData: DTBusiness_User_Invite[]
  ) => {
    setSharedRetailerData(retailerData);
    setSharedBusinessUserData(businessUserData);
  };

  // Format the results
  useEffect(() => {
    const setupCarMonthlyRegistrations = (holdings: DTHoldingProxy[]) => {
      const values: any = {};
      holdings.forEach((holding) => {
        const latestWarranty = BL_Warranty.getLatestWarranty(
          holding.warranties
        );
        //if (latestWarranty?.status === WarrantyStatus.VOIDED) return;
        if (latestWarranty?.status === WarrantyStatus.VOIDED) {
          values['Voided'] = values['Voided'] || 0;
          values['Voided'] = values['Voided'] + 1;
        } else {
          const name = latestWarranty?.templateTitle || '';
          values[name] = values[name] || 0;
          values[name] = values[name] + 1;
        }
      });
      setChartMonthRegistrationsData(
        formatDataChart(
          Object.keys(values).map((name, index) => ({
            id: index,
            count: values[name],
            label: name,
          })),
          'series',
          'Registrations count',
          5
        )
      );
    };

    const setupStonesMonthlyRegistrations = (holdings: any[]) => {
      const values: any = {};
      holdings.forEach((holding) => {
        const name = holding.self_registration.srvInfo.productInfo.typeofStone;
        values[name] = values[name] || 0;
        values[name] = values[name] + 1;
      });
      setChartMonthRegistrationsData(
        formatDataChart(
          Object.keys(values).map((name, index) => ({
            id: index,
            count: values[name],
            label: name,
          })),
          'series',
          'Registrations count',
          5
        )
      );
    };

    const setupChartMontlyRegistrations = (
      // todo: type - see above
      // holdings: ManufacturerHoldingWithVendableData[]
      holdings: any[]
    ) => {
      const monthProductRegistrations: {
        id: any;
        count: number;
        label: any;
      }[] = [];
      const lastMonthTimestamp = Date.now() - 30 * 24 * 60 * 60 * 1000;

      //Filter out SRV holdings
      const filteredHoldings = holdings.filter(
        (holding) => holding.vendable !== SELF_REGISTERED_VENDABLE
      );

      filteredHoldings.forEach((holding, i) => {
        if (holding.register_time >= lastMonthTimestamp) {
          const existingItem = monthProductRegistrations.find(
            (item) => item.label === holding.title
          );
          if (existingItem) {
            existingItem.count++;
          } else {
            monthProductRegistrations.push({
              id: holding.vendable,
              count: 1,
              label: holding.title ?? 'No title',
            });
          }
        }
      });

      const sortedMonthRegistrations = monthProductRegistrations.sort(
        (a, b) => b.count - a.count
      );
      setChartMonthRegistrationsData(
        formatDataChart(
          sortedMonthRegistrations,
          'series',
          'Registrations count',
          5
        )
      );
    };

    // refactor this to count all the ids, and then just fetch labels for the top 5
    const setupChartRetailerRegistrations = async (
      // todo - type, see above
      // holdings: ManufacturerHoldingWithVendableData[]
      holdings: any[]
    ) => {
      const retailerProductRegistrations: ChartCountItemShape[] = [];

      // For retailers that are not in the database and have been submitted by users (purchase_location_other)
      const UNLISTED_RETAILER_ID = '1';

      for (const holding of holdings) {
        // No purchase location handling

        // Database retailer handling
        const existingItem = retailerProductRegistrations.find(
          (item) => item.id === holding.purchase_location
        );
        if (existingItem) {
          existingItem.count++;
        } else {
          retailerProductRegistrations.push({
            id: holding.purchase_location,
            count: 1,
            label: 'No title',
          });

          // Self-registered retailer handling
          const unlistedRetailerItem = retailerProductRegistrations.find(
            (item) => item.id === UNLISTED_RETAILER_ID
          );
          if (holding.purchase_location_other) {
            if (unlistedRetailerItem) {
              unlistedRetailerItem.count++;
            } else {
              retailerProductRegistrations.push({
                id: UNLISTED_RETAILER_ID,
                count: 1,
                label: 'Unlisted',
              });
            }
          }
        }
        if (holding.shopifyLinks) {
          const shopifyExists = retailerProductRegistrations.find(
            (item) => item.id === 'shopify'
          );
          if (shopifyExists) {
            shopifyExists.count++;
          } else {
            retailerProductRegistrations.push({
              id: 'shopify',
              count: 1,
              label: 'Shopify',
            });
          }
        }
      }
      const chartData = await parseRawRetailerCounts(
        retailerProductRegistrations,
        5,
        sharedRetailerData,
        sharedBusinessUserData
      );
      setChartRetailersData(chartData);
    };

    if (!isLoading && holdingList.data) {
      switch (tenantType) {
        case 'carTenants':
          setupCarMonthlyRegistrations(holdingList?.data);
          break;
        case 'tableTenants':
          setupStonesMonthlyRegistrations(holdingList?.data);
          break;
        default:
          setupChartMontlyRegistrations(holdingList?.data);
      }
      void setupChartRetailerRegistrations(holdingList?.data);
    }
  }, [
    holdingList.data,
    isLoading,
    t,
    sharedRetailerData,
    sharedBusinessUserData,
  ]);

  useEffect(() => {
    const getPartnerRetailersList = async () => {
      if (
        config?.HOLDINGS?.REGISTRATION
          ?.SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION
      ) {
        const mainGroupId = activeTenantInfo?.info?.mainGroup;
        if (mainGroupId) {
          try {
            const mainGroup: any = await nestApiGetMainGroup({
              docid: mainGroupId,
            });
            if (mainGroup.sharedLocation && activeTenantInfo?.isPartnerTo) {
              const allRetailers =
                mainGroup?.associatedPartners?.map(
                  (partner: any) =>
                    `S:${partner.tenantLink || partner.businessUserInviteLink}`
                ) || [];
              setFilters((prev) => ({ ...prev, retailers: allRetailers }));
            }
          } catch (error) {
            console.error('Error fetching partner retailers:', error);
          }
        }
      }
    };

    getPartnerRetailersList();
  }, [
    config?.HOLDINGS?.REGISTRATION
      ?.SHOW_PARTNER_REGISTRATION_WITH_SHARED_LOCATION,
    activeTenantInfo,
  ]);

  // Hide PNG/SVG export options from ApexCharts toolbar (keep CSV only)
  useEffect(() => {
    const styleId = 'apexcharts-hide-image-exports';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML =
        '.apexcharts-menu-item.exportSVG, .apexcharts-menu-item.exportPNG { display: none !important; }';
      document.head.appendChild(style);
    }
  }, []);

  if (
    !userPermissions?.includes(Permissions.RegistrationsEdit) &&
    !userPermissions?.includes(Permissions.RegistrationsView) &&
    !userPermissions?.includes(Permissions.Owner)
  )
    return <Navigate to={SAGE_ROUTE_NAME.MANAGE_ACCOUNT} />;

  if (
    !activePersonaIds.repairer &&
    !activePersonaIds.retailer &&
    !activePersonaIds.installer &&
    !activePersonaIds.consumer
  )
    return <LoadingSpinner size="sm" />;

  return (
    <>
      {tenantType === 'carTenants' || tenantType === 'tableTenants' ? (
        <div className="relative">
          <CardWrapperWithHeader
            title={
              filters.dateType === 'This month'
                ? "This month's registrations"
                : filters.dateType === 'Last month'
                ? "Last month's registrations"
                : filters.dateType === '' || filters.dateType === undefined
                ? 'Total registrations'
                : 'Registrations within selected range'
            }
            headerRight={
              holdingList?.data?.length && (
                <div className="text-xl font-bold">
                  Total: {holdingList?.data?.length}
                </div>
              )
            }
          >
            {chartMonthRegistrationsData &&
            chartMonthRegistrationsData.length > 0 ? (
              <Chart
                options={chartOptionsMonthRegistrations}
                series={chartMonthRegistrationsData}
                type="bar"
                height={'250px'}
              />
            ) : (
              <div className="flex h-[250px] items-center justify-center">
                {isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <p className="text-gray-500">No data available</p>
                )}
              </div>
            )}
          </CardWrapperWithHeader>
        </div>
      ) : (
        <div
          className={`mb-5 grid gap-5 ${
            !activePersonaIds.consumer
              ? 'grid-cols-1 md:grid-cols-2'
              : 'grid-cols-1'
          }`}
        >
          <CardWrapperWithHeader title={t('message.productRegistrations')}>
            {chartMonthRegistrationsData &&
            chartMonthRegistrationsData.length > 0 ? (
              <Chart
                options={chartOptionsMonthRegistrations}
                series={chartMonthRegistrationsData}
                type="bar"
                height={'250px'}
              />
            ) : (
              <div className="flex h-[250px] items-center justify-center">
                {isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <p className="text-gray-500">No data available</p>
                )}
              </div>
            )}
          </CardWrapperWithHeader>
          {!activePersonaIds.consumer && (
            <CardWrapperWithHeader title={t('message.registrationByChannel')}>
              <Chart
                options={chartOptionsRetailers}
                series={chartRetailersData}
                type="bar"
                height={'250px'}
              />
            </CardWrapperWithHeader>
          )}
        </div>
      )}
      <div className="relative z-10 flex w-full items-center justify-between py-4">
        <TableRegistrations
          isLoading={isLoading}
          tenantType={tenantType}
          holdingList={holdingList}
          filters={filters}
          onFilter={onChangeFilters}
          isRegularTenant={!isPremiumTenant}
          onRetailerDataLoaded={handleRetailerDataLoaded}
        />
      </div>
    </>
  );
}

export default RegistrationsView;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const parseRawRetailerCounts = async (
  rawData: ChartCountItemShape[],
  limit: number,
  sharedRetailerData?: any,
  sharedBusinessUserData?: any
) => {
  const sortedData = rawData.sort((a, b) => b.count - a.count);

  const topRetailers = sortedData.slice(0, limit + 3);

  topRetailers.forEach((retailer) => {
    const retailerId = retailer.id;
    if (retailerId) {
      const retailerParts = retailerId.split(':');

      if (retailerParts?.length > 1) {
        const retailerInfo = sharedRetailerData?.find(
          (r: any) => r.docid === retailerId
        );
        if (retailerInfo) {
          retailer.label = retailerInfo.name || 'No title';
        }
      } else {
        const invite = sharedBusinessUserData?.find(
          (i: any) => i.email === retailerId
        );
        if (invite) {
          retailer.label = invite.orgName || 'No title';
        }
      }
    }
  });

  // Filter out the ones with no titles
  const filteredTopRetailers: ChartCountItemShape[] = topRetailers.filter(
    (retailer) => retailer.label !== 'No title'
  );

  return formatDataChart(
    filteredTopRetailers,
    'series',
    'Registrations count',
    limit
  );
};
